import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Pagination } from '@thinknimble/tn-models'
import { usePagination } from './use-pagination'

interface UseInfinitePaginationOptions {
  /** Items per page (also used as the initial Pagination size) */
  pageSize: number
  /**
   * Changing this value clears the appended list and sends the user back to
   * page 1. Use this to reset the strip when an upstream filter (e.g. the
   * selected organization) changes.
   */
  resetKey?: string | number | null
}

interface UseInfinitePaginationReturn<T> {
  /** Concatenated items across all currently-loaded pages */
  items: T[]
  /**
   * Callback ref to attach to a trailing `<div>` to drive
   * IntersectionObserver-based loading. Pass directly as `ref={sentinelRef}`.
   */
  sentinelRef: (node: HTMLDivElement | null) => void
  /** Pagination state to feed into your `useQuery` */
  pagination: Pagination
  /** Pagination callback to feed into your `useQuery` */
  paginationCallback: (pagination: Pagination) => void
  /** Apply the latest paged response from your query (call inside a useEffect) */
  applyData: (data: { results: T[] } | undefined) => void
  /** True when the next page is currently in flight (drives the trailing spinner) */
  isLoadingNextPage: boolean
  /** Set externally so the observer can avoid firing during a fetch */
  setIsFetching: (fetching: boolean) => void
  /** Jump to a specific page; clears appended state */
  goToPage: (page: number) => void
  /** Next page (button form); clears appended state */
  goToNextPage: () => void
  /** Previous page (button form); clears appended state */
  goToPrevPage: () => void
}

/**
 * Wraps `usePagination` with append-on-scroll behavior.
 *
 * - Returns a `sentinelRef` to attach to a trailing `<div>` at the right edge
 *   of a horizontally-scrolling strip. When that sentinel intersects the
 *   viewport and there is another page available, the next page is fetched
 *   automatically and appended to `items`.
 * - The standard `goToPage` / `goToNextPage` / `goToPrevPage` actions clear
 *   the appended list and start fresh from the requested page, so a regular
 *   `<Pagination>` component layered on top behaves as expected.
 * - When `resetKey` changes, the appended list is cleared and pagination
 *   resets to page 1.
 *
 * Usage:
 * ```tsx
 * const infinite = useInfinitePagination<Org>({ pageSize: 6 })
 * const { data, isFetching } = useQuery(orgQueries.list({
 *   pagination: infinite.pagination,
 *   paginationCallback: infinite.paginationCallback,
 * }))
 * useEffect(() => infinite.applyData(data), [data, infinite.applyData])
 * useEffect(() => infinite.setIsFetching(isFetching), [isFetching, infinite.setIsFetching])
 * ```
 */
export function useInfinitePagination<T>({
  pageSize,
  resetKey,
}: UseInfinitePaginationOptions): UseInfinitePaginationReturn<T> {
  const initialPagination = useMemo(() => new Pagination({ size: pageSize }), [pageSize])

  const {
    pagination,
    paginationCallback,
    goToNextPage: rawGoToNextPage,
    goToPrevPage: rawGoToPrevPage,
    goToPage: rawGoToPage,
  } = usePagination({ initialPagination })

  // Map of page number → results array for that page
  const [pages, setPages] = useState<Record<number, T[]>>({})
  // First page in the visible window. The window is contiguous from
  // windowStart up to pagination.page.
  const [windowStart, setWindowStart] = useState(1)

  // Callback ref so the observer effect can react to sentinel attachment.
  // (A plain useRef wouldn't trigger a re-run when the consumer mounts the
  // sentinel node.)
  const [sentinelNode, setSentinelNode] = useState<HTMLDivElement | null>(null)
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    setSentinelNode(node)
  }, [])

  // Track current page in a ref so applyData() / setIsFetching() can stay
  // referentially stable (avoiding re-renders in consumer useEffects).
  const pageRef = useRef(pagination.page)
  useEffect(() => {
    pageRef.current = pagination.page
  }, [pagination.page])

  // External fetching state. Stored in both a ref (for the observer) and
  // state (so the trailing spinner re-renders).
  const isFetchingRef = useRef(false)
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false)

  // Reset on resetKey change (skip the very first run on mount)
  const initialized = useRef(false)
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true
      return
    }
    setPages({})
    setWindowStart(1)
    rawGoToPage(1)
  }, [resetKey, rawGoToPage])

  // Apply incoming page data into the pages map. Stable across renders so
  // consumer useEffect deps stay clean.
  const applyData = useCallback((data: { results: T[] } | undefined) => {
    if (!data?.results) return
    const currentPage = pageRef.current
    setPages((prev) => {
      if (prev[currentPage] === data.results) return prev
      return { ...prev, [currentPage]: data.results }
    })
  }, [])

  // Mirror the consumer's `isFetching` into the hook so the observer can
  // skip firing during in-flight fetches and the trailing spinner is shown
  // only when actually loading a *next* page (not the very first page).
  const setIsFetching = useCallback((fetching: boolean) => {
    isFetchingRef.current = fetching
    // Only show the trailing spinner when we're loading a page beyond the
    // first (i.e. we already have some items rendered).
    setIsLoadingNextPage(fetching && pageRef.current > 1)
  }, [])

  // Visible items = concat of pages from windowStart up to current pagination.page
  const items = useMemo(() => {
    const result: T[] = []
    for (let p = windowStart; p <= pagination.page; p++) {
      const slice = pages[p]
      if (slice) result.push(...slice)
    }
    return result
  }, [pages, windowStart, pagination.page])

  // IntersectionObserver: when the sentinel scrolls into view and there is a
  // next page, advance pagination.page (which triggers a refetch via the
  // consumer's useQuery and the new page gets appended through applyData).
  useEffect(() => {
    if (!sentinelNode) return
    if (!pagination.hasNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && pagination.hasNextPage && !isFetchingRef.current) {
          rawGoToNextPage()
        }
      },
      { threshold: 0.1 },
    )
    observer.observe(sentinelNode)
    return () => observer.disconnect()
  }, [sentinelNode, pagination.hasNextPage, rawGoToNextPage, pagination.page])

  // Public navigation: clear appended state and start fresh from the
  // requested page (per the spec).
  const goToPage = useCallback(
    (page: number) => {
      setPages({})
      setWindowStart(page)
      rawGoToPage(page)
    },
    [rawGoToPage],
  )

  const goToNextPage = useCallback(() => {
    if (!pagination.hasNextPage) return
    setPages({})
    setWindowStart(pagination.page + 1)
    rawGoToNextPage()
  }, [rawGoToNextPage, pagination.page, pagination.hasNextPage])

  const goToPrevPage = useCallback(() => {
    if (pagination.page <= 1) return
    setPages({})
    setWindowStart(pagination.page - 1)
    rawGoToPrevPage()
  }, [rawGoToPrevPage, pagination.page])

  return {
    items,
    sentinelRef,
    pagination,
    paginationCallback,
    applyData,
    isLoadingNextPage,
    setIsFetching,
    goToPage,
    goToNextPage,
    goToPrevPage,
  }
}
